import React from 'react';
import { User, ChevronLeft, ChevronRight, Calendar, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SidebarContext } from '@/components/image-lightbox/types';
import { CommunityProfilePicture, CommunityProfileContext } from '@/types/community-profile';
import { formatDistanceToNow } from 'date-fns';

interface CommunityProfileSidebarProps {
  context: SidebarContext<CommunityProfilePicture>;
  profileContext: CommunityProfileContext;
}

export const CommunityProfileSidebar: React.FC<CommunityProfileSidebarProps> = ({
  context,
  profileContext
}) => {
  const {
    item,
    hasPrevious,
    hasNext,
    isLoadingMore,
    hasNextPage,
    onPrevious,
    onNext,
    onLoadMore
  } = context;

  const { user, discussionId, replyId, isAuthor } = profileContext;

  return (
    <div className="w-full md:w-[280px] flex-shrink-0 md:border-l border-accent/10 flex flex-col md:h-full">
      {/* Main content container */}
      <div className="flex flex-col md:h-full">
        {/* Navigation buttons at the top */}
        <div className="border-b border-accent/10 bg-background">
          <div className="p-3 md:p-4 flex gap-2">
            <motion.button
              className={cn(
                "flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all flex-1",
                hasPrevious
                  ? "bg-black hover:bg-gray-900 text-white dark:bg-black dark:hover:bg-gray-900 dark:text-white cursor-pointer"
                  : "bg-gray-800 text-gray-400 dark:bg-gray-800 dark:text-gray-600 opacity-50 cursor-not-allowed"
              )}
              onClick={hasPrevious ? onPrevious : undefined}
              disabled={!hasPrevious}
              whileHover={hasPrevious ? { scale: 1.02 } : {}}
              whileTap={hasPrevious ? { scale: 0.98 } : {}}
              data-test-id="sidebar-previous-button"
              type="button"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </motion.button>
            
            <motion.button
              className={cn(
                "flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all flex-1",
                (hasNext && !isLoadingMore)
                  ? "bg-black hover:bg-gray-900 text-white dark:bg-black dark:hover:bg-gray-900 dark:text-white cursor-pointer"
                  : isLoadingMore
                    ? "bg-gray-700 text-white dark:bg-gray-700 dark:text-white cursor-default"
                    : "bg-gray-800 text-gray-400 dark:bg-gray-800 dark:text-gray-600 opacity-50 cursor-not-allowed"
              )}
              onClick={() => {
                if (hasNext && onNext && !isLoadingMore) {
                  onNext();
                } else if (hasNextPage && !isLoadingMore && onLoadMore) {
                  onLoadMore();
                }
              }}
              disabled={(!hasNext && !hasNextPage) || isLoadingMore}
              whileHover={(hasNext && !isLoadingMore) ? { scale: 1.02 } : {}}
              whileTap={(hasNext && !isLoadingMore) ? { scale: 0.98 } : {}}
              data-test-id="sidebar-next-button"
              type="button"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </motion.button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 p-3 md:p-4 md:overflow-y-auto">
          {/* User Profile Info */}
          <div className="space-y-4">
            {/* User Header */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                  {user.first_name || 'Community Member'}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Profile Picture
                </p>
              </div>
            </div>

            {/* User Status */}
            {isAuthor && (
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                <MessageSquare className="h-3 w-3 mr-1" />
                Post Author
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
}; 